import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { PagePermission, PermissionAction, PermissionLevel } from '../types/Authorization';
import { getReq } from './request';
import { useUserPermissions } from '../contexts/UserPermissionsContext';
import { useEffect, useState } from 'react';


export const DefineAbilitiesForCurrentUser =  async (pageName: string,currentUser:any): Promise<MongoAbility> => {
    const { can, cannot, build } = new AbilityBuilder(createMongoAbility)
    //const currentUser = useUserPermissions();
    const filteredPages = currentUser.userCallPermissions.pages.filter(
        (page:any) => page.name.toLowerCase() === pageName.toLowerCase()
    );
    if (filteredPages.length > 0) {
        const page = filteredPages[0];
        if (page) {
            const permission = page.permissions as unknown as PagePermission;

            // Delete Region
            // user can delete all entities
            if (permission.delete === PermissionLevel.All) {
                can(PermissionAction.DELETE, pageName);
            }
            // Office (People and HR pages): the server narrows to the role's offices.
            if (permission.delete === PermissionLevel.Office) {
                can(PermissionAction.DELETE, pageName);
            }
            // user can delete their own created entities
            if (permission.delete === PermissionLevel.Own) {
                can(PermissionAction.DELETE, pageName, { createdBy: currentUser.userCallPermissions.userId });
            }

            // user can delete any entity created by their teams members
            if (permission.delete === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status ===200){
                    const userIds:number[]= teamResult.body;
                    can(PermissionAction.DELETE, pageName, { createdBy:{ $in: userIds } })
                }
            }
            // user cannot delete any entity
            if (permission.delete === PermissionLevel.No) {
                cannot(PermissionAction.DELETE, pageName);
            }

            // Restore Region
            // user can restore all entities
            if (permission.restore === PermissionLevel.All) {
                can(PermissionAction.RESTORE, pageName);
            }
            // Office (People and HR pages): the server narrows to the role's offices.
            if (permission.restore === PermissionLevel.Office) {
                can(PermissionAction.RESTORE, pageName);
            }
            // user can restore their own created entities
            if (permission.restore === PermissionLevel.Own) {
                can(PermissionAction.RESTORE, pageName, { createdBy: currentUser.userCallPermissions.userId });
            }

            // user can restore any entity created by their teams members
            if (permission.restore === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status ===200){
                    const userIds:number[]= teamResult.body;
                    can(PermissionAction.RESTORE, pageName, { createdBy:{ $in: userIds } })
                }
            }
            // user cannot restore any entity
            if (permission.restore === PermissionLevel.No) {
                cannot(PermissionAction.RESTORE, pageName);
            }

            // edit Region
            // user can edit all entities
            if (permission.edit === PermissionLevel.All) {
                can(PermissionAction.EDIT, pageName);
            }
            // Office (People and HR pages): the server narrows to the role's offices.
            if (permission.edit === PermissionLevel.Office) {
                can(PermissionAction.EDIT, pageName);
            }
            // user can edit their own created entities
            if (permission.edit === PermissionLevel.Own) {
                can(PermissionAction.EDIT, pageName, { createdBy: currentUser.userCallPermissions.userId });
            }

            // user can edit any entity created by their teams members
            if (permission.edit === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status ===200){
                    const userIds:number[]= teamResult.body;
                    can(PermissionAction.EDIT, pageName, { createdBy:{ $in: userIds } })
                }
            }
            // user cannot edit any entity
            if (permission.edit === PermissionLevel.No) {
                cannot(PermissionAction.EDIT, pageName);
            }

            // Create Region
            // user can create all entities
            if (permission.create === PermissionLevel.Yes) {
                can(PermissionAction.CREATE, pageName);
            }

            // logs Region
            // user can view logs
            if (permission.logs === PermissionLevel.Yes) {
                can(PermissionAction.LOGS, pageName);
            }
            // export Region
            // user can export — export is a yes/no action in the Roles UI, but
            // roles seeded or edited outside it can carry all/team/own. The BE
            // authorizes any level except No, so mirror that here: matching only
            // "yes" hid the export buttons from users the BE would allow.
            if (permission.export !== undefined && permission.export !== PermissionLevel.No) {
                can(PermissionAction.EXPORT, pageName);
            }

            // Request Region
            // user can submit requests for services/incidents/expenses
            if (permission.request === PermissionLevel.Yes) {
                can(PermissionAction.REQUEST, pageName);
            }
            if (permission.request === PermissionLevel.No) {
                cannot(PermissionAction.REQUEST, pageName);
            }

            // Invite Region
            // user can send mobile-app invites (only meaningful on the Users page today)
            if (permission.invite === PermissionLevel.Yes) {
                can(PermissionAction.INVITE, pageName);
            }
            if (permission.invite === PermissionLevel.No) {
                cannot(PermissionAction.INVITE, pageName);
            }

            // Dashboard Region
            // user can view all dashboard data
            if (permission.dashboard === PermissionLevel.All) {
                can(PermissionAction.DASHBOARD, pageName);
            }
            // user can view their own dashboard data
            if (permission.dashboard === PermissionLevel.Own) {
                can(PermissionAction.DASHBOARD, pageName, { createdBy: currentUser.userCallPermissions.userId });
            }
            // user can view team dashboard data
            if (permission.dashboard === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status ===200){
                    const userIds:number[]= teamResult.body;
                    can(PermissionAction.DASHBOARD, pageName, { createdBy:{ $in: userIds } })
                }
            }
            // user cannot view dashboard
            if (permission.dashboard === PermissionLevel.No) {
                cannot(PermissionAction.DASHBOARD, pageName);
            }

            // Report Region
            // user can view all report data
            if (permission.report === PermissionLevel.All) {
                can(PermissionAction.REPORT, pageName);
            }
            // user can view their own report data
            if (permission.report === PermissionLevel.Own) {
                can(PermissionAction.REPORT, pageName, { createdBy: currentUser.userCallPermissions.userId });
            }
            // user can view team report data
            if (permission.report === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status ===200){
                    const userIds:number[]= teamResult.body;
                    can(PermissionAction.REPORT, pageName, { createdBy:{ $in: userIds } })
                }
            }
            // user cannot view reports
            if (permission.report === PermissionLevel.No) {
                cannot(PermissionAction.REPORT, pageName);
            }

            // Approve Region
            // user can approve all requests
            if (permission.approve === PermissionLevel.All) {
                can(PermissionAction.APPROVE, pageName);
            }
            // user can approve when permission is set to yes
            if (permission.approve === PermissionLevel.Yes) {
                can(PermissionAction.APPROVE, pageName);
            }
            // user can approve requests from team members
            if (permission.approve === PermissionLevel.Team) {
                const teamResult = await getReq('user-teams/'+ currentUser.userCallPermissions.userId);
                if(teamResult.status === 200){
                    const userIds: number[] = teamResult.body;
                    can(PermissionAction.APPROVE, pageName, { requestedBy: { $in: userIds } });
                }
            }
            // user cannot approve any requests
            if (permission.approve === PermissionLevel.No) {
                cannot(PermissionAction.APPROVE, pageName);
            }
        }
    }
    return build();
};

/**
 * The user's CASL ability for one page, or `null` while it is still loading —
 * callers must guard (`if (abilities)` / `abilities?.can`) before querying it.
 */
export const useAbilities = (pageName:string): MongoAbility | null => {
    const userPermissions = useUserPermissions();
    const [abilities, setAbilities] = useState<MongoAbility | null>(null);

    useEffect(() => {
        const fetchAbilities = async () => {
            const abilitiesResult = await DefineAbilitiesForCurrentUser(pageName, userPermissions);
            setAbilities(abilitiesResult);
        };

        fetchAbilities();
    }, [pageName, userPermissions]);

    return abilities;
};